// Bản đồ nhỏ cạnh ô địa chỉ ở form tạo đơn: thấy ngay điểm giao. Có ghim (chọn trên bản đồ / địa chỉ
// đã lưu có toạ độ) → đúng điểm đó; chưa ghim → vị trí ước tính theo địa chỉ đang nhập (/geo/geocode,
// chỉ để xem, không gửi kèm đơn). Bấm "Chọn trên bản đồ" mở AddressMapPickerModal để ghim chính xác.
// OpenStreetMap qua Leaflet (không cần key) — ghim vẽ bằng lớp phủ ở tâm như hộp chọn bản đồ.

import { useEffect, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { Crosshair, MapPin } from "lucide-react"
import { get } from "@/lib/api"
import { useDebounced } from "@/hooks/useDebounced"
import { cn } from "@/lib/utils"

const VIETNAM: L.LatLngTuple = [16.2, 106.3]

/** Chuỗi tra vị trí từ chi tiết tới thô: số nhà → phường → quận → tỉnh (dịch vụ tra không ra số nhà thì vẫn có khu vực). */
export function geocodeCandidates(a: { street?: string; ward?: string; district?: string; city?: string }): string[] {
  const join = (...xs: (string | undefined)[]) => xs.map((x) => x?.trim()).filter(Boolean).join(", ")
  return [join(a.street, a.ward, a.district, a.city), join(a.ward, a.district, a.city), join(a.district, a.city), join(a.city)].filter(
    (q, i, all) => q.length >= 3 && all.indexOf(q) === i,
  )
}

export function AddressMiniMap({
  pin,
  address,
  onOpenPicker,
  onClearPin,
  className,
}: {
  pin: { lat: number; lng: number } | null
  /** Địa chỉ đang nhập — để ước tính vị trí khi chưa ghim */
  address: { street?: string; ward?: string; district?: string; city?: string }
  onOpenPicker: () => void
  onClearPin: () => void
  className?: string
}) {
  const boxRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)

  const candidates = useDebounced(geocodeCandidates(address).join("|"), 700)
  const { data: guess, isFetching } = useQuery({
    queryKey: ["geo", "geocode", candidates],
    queryFn: async () => {
      for (const q of candidates.split("|")) {
        const hit = await get<{ lat: number; lng: number }>(`/geo/geocode?address=${encodeURIComponent(q)}`).catch(() => null)
        if (hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) return { ...hit, exact: q === candidates.split("|")[0] }
      }
      return null
    },
    enabled: !pin && Boolean(candidates),
    staleTime: Infinity,
  })
  const point = pin ?? (guess ? { lat: guess.lat, lng: guess.lng } : null)

  useEffect(() => {
    const el = boxRef.current
    if (!el || mapRef.current) return
    const map = L.map(el, { center: VIETNAM, zoom: 5, zoomControl: false, scrollWheelZoom: false, attributionControl: true })
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map)
    mapRef.current = map
    // Khung đổi kích thước theo bố cục (cột hẹp / rộng) → vẽ lại ô bản đồ
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(el)
    return () => {
      ro.disconnect()
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Ghim: sát điểm; ước tính đúng số nhà: gần; chỉ ra khu vực (phường / quận / tỉnh): xa hơn
    if (point) map.setView([point.lat, point.lng], pin ? 17 : guess?.exact ? 16 : 13, { animate: false })
    else map.setView(VIETNAM, 5, { animate: false })
  }, [point?.lat, point?.lng, pin, guess?.exact])

  const caption = pin
    ? "Đã ghim vị trí giao"
    : point
      ? guess?.exact ? "Vị trí ước tính theo địa chỉ" : "Khu vực giao"
      : isFetching ? "Đang tìm vị trí…" : "Nhập địa chỉ để xem vị trí"

  return (
    <div className={cn("relative isolate overflow-hidden rounded-lg border border-rule bg-paper", className)}>
      <div ref={boxRef} className="absolute inset-0 z-0" aria-hidden />
      {point && (
        <MapPin
          className={cn("pointer-events-none absolute left-1/2 top-1/2 z-[401] size-7 -translate-x-1/2 -translate-y-full drop-shadow", pin ? "fill-accent/30 text-accent" : "fill-warn/20 text-warn")}
          aria-hidden
        />
      )}
      <div className="absolute inset-x-2 bottom-2 z-[401] flex flex-wrap items-center gap-1.5">
        <span className="rounded-md bg-raise/90 px-2 py-1 text-xs text-inkSoft shadow-sm backdrop-blur">
          {caption}
          {pin && (
            <button type="button" onClick={onClearPin} className="ml-1.5 cursor-pointer text-muted underline hover:text-danger">
              Bỏ ghim
            </button>
          )}
        </span>
        <button
          type="button"
          onClick={onOpenPicker}
          className="ml-auto inline-flex min-h-11 desktop:min-h-0 cursor-pointer items-center gap-1 rounded-md bg-accent px-2.5 py-1.5 text-xs font-medium text-onAccent shadow-sm hover:bg-accent/90"
        >
          <Crosshair className="size-3.5" /> {pin ? "Đổi vị trí" : "Ghim trên bản đồ"}
        </button>
      </div>
    </div>
  )
}
